import { useMutation } from '@apollo/client';
import { createForm } from '@seek/forms-ui';
import {
  Box,
  Divider,
  Heading,
  Hidden,
  List,
  Page,
  PageBlock,
  Stack,
  Strong,
  Text,
  useToast,
} from 'braid-design-system';
import { useRef, useState } from 'react';
import Youtube from 'react-youtube';

import { SiteWideFooter } from 'src/components/SiteWideFooter';
import {
  MarketplaceInsightsSignupDocument,
  type MarketplaceInsightsSignupInput,
  type UserRegistrationType,
} from 'src/generated/graphql';

import { SalesCollateralCta } from './components/SalesCollateralCta';
import { SignupForm } from './components/SignupForm';
import { Success } from './components/Success';
import CoverImage from './resources/2025-MarketplaceTrends.png';

import * as styles from './MarketplaceInsights.css';

export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  userType: UserRegistrationType | undefined;
  checkbox: boolean;
}

export const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  userType: undefined,
  checkbox: false,
};

export const { FormProvider, useField } = createForm<FormValues>(
  'marketplaceInsightsReportSignup',
);

const MarketplaceInsights = () => {
  const [signup, { loading }] = useMutation(MarketplaceInsightsSignupDocument);
  const [formSubmitted, setFormSubmitted] = useState<
    | { kind: 'idle' }
    | { kind: 'submitted'; userRegistrationType: UserRegistrationType }
  >({ kind: 'idle' });
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const showToast = useToast();

  const onSubmit = async (formData: FormValues) => {
    if (formData.checkbox === false || formData.userType === undefined) {
      return;
    }

    const signupInput: MarketplaceInsightsSignupInput = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailAddress: formData.email,
      userRegistrationType: formData.userType,
    };

    try {
      await signup({ variables: { input: signupInput } });
    } catch {
      showToast({
        tone: 'critical',
        message: 'There was an unexpected issue, please contact our support',
      });
      return {};
    }

    contentContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    setFormSubmitted({
      kind: 'submitted',
      userRegistrationType: formData.userType,
    });
    return {};
  };

  return (
    <Page footer={<SiteWideFooter />}>
      <Hidden below="tablet">
        <Divider />
      </Hidden>

      <Box paddingTop="xlarge">
        <PageBlock width="medium">
          <Box ref={contentContainerRef}>
            <Stack space={{ mobile: 'small', tablet: 'large' }}>
              <Box display={{ mobile: 'block', tablet: 'flex' }}>
                <Box marginRight={{ mobile: 'none', tablet: 'xlarge' }}>
                  {formSubmitted.kind === 'submitted' ? (
                    <Success />
                  ) : (
                    <Stack space="xxlarge">
                      <Stack space="xlarge">
                        <Stack space="medium">
                          <Text>EXCLUSIVE MARKET INSIGHTS</Text>

                          <Heading level="2" component="h1">
                            Unlock your competitive edge
                          </Heading>
                        </Stack>

                        <Stack space="medium">
                          <Text>
                            Get ahead of the curve with the{' '}
                            <Strong>
                              SEEK Business Marketplace Insights Report
                            </Strong>{' '}
                            — your go-to resource for key trends and expert
                            analysis in the business-for-sale market.
                          </Text>

                          <List>
                            <Text>
                              <Strong>Key Market Trends</Strong> — State by
                              state, industry and price demand trends
                            </Text>
                            <Text>
                              <Strong>Expert Analysis</Strong> — Economic
                              analysis from SEEK&apos;s Senior Economist
                            </Text>
                            <Text>
                              <Strong>Actionable Insights</Strong> — Predictions
                              to help you plan ahead
                            </Text>
                          </List>

                          <Text>
                            Sign up now and stay ahead of the competition!
                          </Text>
                        </Stack>
                      </Stack>

                      <FormProvider initialValues={initialValues}>
                        {(props) => (
                          <SignupForm
                            {...props}
                            onSubmit={onSubmit}
                            loading={loading}
                          />
                        )}
                      </FormProvider>

                      <Youtube
                        videoId="iHOyttMvpm4"
                        opts={{ height: '250px', width: '100%' }}
                        className={styles.iframe}
                      />
                    </Stack>
                  )}
                </Box>

                <Box
                  display={{
                    mobile: 'none',
                    tablet: 'block',
                  }}
                  style={{ minHeight: 966, minWidth: 400, width: '100%' }}
                >
                  <img
                    src={CoverImage}
                    className={styles.coverImage}
                    alt="Seek Business marketplace insights enquiry trends"
                  />
                </Box>
              </Box>

              {formSubmitted.kind === 'submitted' ? (
                <SalesCollateralCta
                  userRegistrationType={formSubmitted.userRegistrationType}
                />
              ) : null}
            </Stack>
          </Box>
        </PageBlock>
      </Box>
    </Page>
  );
};

export default MarketplaceInsights;
